import { Icon } from '@iconify/react/dist/iconify.js';
import { useState, useEffect, useRef, useCallback } from 'react';
import IntakeService from '../../../../services/IntakeService';
import useAuthStore from '../../../../store/Auth';
import { formatTimestamp } from '../../../../utils/helpers/helpers';
import { Profile } from '../../../../svgs/svgs';
import type { Intake, IntakeComment } from '@/types';
import GlobalButton from '@/components/ui/GlobalButton';
import useBackendPagination from '@/hooks/useBackendPagination';
import PaginationArrows from '@/components/ui/PaginationArrows/PaginationArrows';
import {
  CommentAttachButton,
  StagedFileList,
} from '@/components/common/CommentAttachmentTray/CommentAttachmentTray';
import CommentAttachmentsList from '@/components/common/CommentAttachmentTray/CommentAttachmentsList';
import AttachmentService from '@/services/AttachmentService';
import { useToast } from '@/context/ToastContext';

const COMMENTS_PER_PAGE = 5;

interface IntakeCommentsProps {
  intake: Intake | null;
}

export default function IntakeComments({ intake }: IntakeCommentsProps) {
  const { user } = useAuthStore();
  const { addToast } = useToast();

  const [isFocused, setIsFocused] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasCommentText, setHasCommentText] = useState(false);
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const [isUploadingAttachments, setIsUploadingAttachments] = useState(false);
  const [deletingCommentId, setDeletingCommentId] = useState<string | null>(
    null
  );
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [intakeComments, setIntakeComments] = useState<IntakeComment[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const editEditorRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);

  const {
    currentPage,
    hasPrevious,
    hasNext,
    showingFrom,
    showingTo,
    totalItems,
    goToNextPage,
    goToPreviousPage,
    setCurrentPage,
  } = useBackendPagination({ totalItems: totalCount }, COMMENTS_PER_PAGE, 1);

  const refetchComments = async (page: number) => {
    if (!intake?._id) return;
    try {
      const res = await IntakeService.getIntakeComments(intake._id, {
        page,
        limit: COMMENTS_PER_PAGE,
      });
      setIntakeComments(res?.data ?? []);
      setTotalCount(res?.meta?.total ?? res?.count ?? 0);
    } catch (error) {
      console.error('Error fetching intake comments:', error);
    }
  };

  useEffect(() => {
    if (intake?._id) {
      refetchComments(1);
    }
  }, [intake?._id]);

  const handleNextPage = () => {
    const next = currentPage + 1;
    goToNextPage();
    refetchComments(next);
  };

  const handlePreviousPage = () => {
    const prev = currentPage - 1;
    goToPreviousPage();
    refetchComments(prev);
  };

  const execCommand = (
    command: string,
    value: string | undefined = undefined
  ) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  };

  const handleSubmit = async (e?: any) => {
    e?.preventDefault();
    const text = editorRef.current?.innerText.trim();
    const html = editorRef.current?.innerHTML;

    if ((!text && stagedFiles.length === 0) || isSubmitting || !intake?._id)
      return;

    setIsSubmitting(true);

    try {
      const created = await IntakeService.addIntakeComment(intake._id, {
        intake_comment_text: html || text || '',
      });

      // The create endpoint returns { success, message, data: { _id } }.
      const newCommentId =
        (created as any)?.data?._id ??
        (created as any)?._id ??
        (created as any)?.data?.comment?._id;
      if (stagedFiles.length > 0 && newCommentId && intake?._id) {
        setIsUploadingAttachments(true);
        try {
          const results = await AttachmentService.uploadMany(
            {
              type: 'intake_comment',
              intakeId: intake._id,
              commentId: newCommentId,
            },
            stagedFiles
          );
          const failed = results.filter((r) => !r.ok).length;
          if (failed > 0) {
            addToast(
              `${failed} of ${results.length} attachment${results.length > 1 ? 's' : ''} failed to upload.`,
              'error',
              5000
            );
          }
        } finally {
          setIsUploadingAttachments(false);
        }
      }

      if (editorRef.current) editorRef.current.innerHTML = '';
      setHasCommentText(false);
      setStagedFiles([]);

      setCurrentPage(1);
      await refetchComments(1);
    } catch (error) {
      console.error('Error creating comment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleCommentInput = () => {
    const text = editorRef.current?.innerText.trim();
    setHasCommentText(!!text);
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!intake?._id) return;

    setDeletingCommentId(commentId);
    try {
      await IntakeService.deleteIntakeComment(intake._id, commentId);
      setCurrentPage(1);
      await refetchComments(1);
    } catch (error) {
      console.error('Error deleting comment:', error);
    } finally {
      setDeletingCommentId(null);
      setConfirmDeleteId(null);
    }
  };

  const handleStartEdit = (comment: IntakeComment) => {
    setEditingCommentId(comment._id);
    setTimeout(() => {
      if (editEditorRef.current) {
        editEditorRef.current.innerHTML = comment.intake_comment_text;
      }
    }, 0);
  };

  const handleUpdateComment = async (commentId: string) => {
    if (!intake?._id) return;

    const text = editEditorRef.current?.innerText.trim();
    const html = editEditorRef.current?.innerHTML;

    if (!text || !html) {
      return;
    }

    setDeletingCommentId(commentId);
    try {
      await IntakeService.updateIntakeComment(intake._id, commentId, {
        intake_comment_text: html,
      });
      setCurrentPage(1);
      await refetchComments(1);
    } catch (error) {
      console.error('Error updating comment:', error);
    } finally {
      setDeletingCommentId(null);
      setEditingCommentId(null);
    }
  };

  return (
    <div className="w-full">
      <div className="w-full flex items-center gap-4 justify-between mb-2">
        <h3 className="text-sm 2xl:text-base text-input-active-label font-semibold">
          Comments
          {totalCount > 0 && (
            <span className="ml-1">({totalCount})</span>
          )}
        </h3>
      </div>

      <div className="bg-modl-input border-0.5 border-gray-3/20 p-3 flex flex-col rounded-lg">
        {/* Comment Input */}
        <div
          className={`mb-4 border-0.5 rounded-lg bg-modal-input transition-colors ${
            isFocused ? 'border-gray-3/40' : 'border-gray-3/20'
          }`}
        >
          <div className="p-3">
            <div
              ref={editorRef}
              contentEditable
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              onInput={handleCommentInput}
              onKeyDown={handleKeyDown}
              className="w-full bg-transparent text-xs text-text-primary-highlight outline-none min-h-[60px] empty:before:content-[attr(data-placeholder)] empty:before:text-input-inactive-placeholder"
              data-placeholder="Write a comment"
              suppressContentEditableWarning
            />
          </div>

          {/* Staged attachments */}
          <StagedFileList
            files={stagedFiles}
            onRemove={(idx) =>
              setStagedFiles((prev) => prev.filter((_, i) => i !== idx))
            }
            uploading={isUploadingAttachments}
          />

          {/* Toolbar */}
          <div className="border-t border-gray-3/20 px-3 py-2 flex items-center justify-between gap-3">
            <div className="flex items-center gap-1">
              <button
                onClick={() => execCommand('bold')}
                className="p-1.5 hover:bg-[#141414] rounded text-text-primary-body hover:text-gray-300 transition-colors"
                title="Bold"
                type="button"
              >
                <Icon icon="lucide:bold" width={16} height={16} />
              </button>
              <button
                onClick={() => execCommand('italic')}
                className="p-1.5 hover:bg-[#141414] rounded text-text-primary-body hover:text-gray-300 transition-colors"
                title="Italic"
                type="button"
              >
                <Icon icon="lucide:italic" width={16} height={16} />
              </button>
              <button
                onClick={() => execCommand('underline')}
                className="p-1.5 hover:bg-[#141414] rounded text-text-primary-body hover:text-gray-300 transition-colors"
                title="Underline"
                type="button"
              >
                <Icon icon="lucide:underline" width={16} height={16} />
              </button>
              <CommentAttachButton
                onFiles={(files) =>
                  setStagedFiles((prev) => [...prev, ...files])
                }
                disabled={isSubmitting || isUploadingAttachments}
              />
            </div>

            <div className="flex items-center gap-3">
              <GlobalButton
                size="sm"
                onClick={handleSubmit}
                state={
                  isSubmitting ||
                  (!hasCommentText && stagedFiles.length === 0)
                    ? 'inactive'
                    : 'default'
                }
                disabled={
                  isSubmitting ||
                  (!hasCommentText && stagedFiles.length === 0)
                }
                title="Send (Ctrl+Enter)"
                leftSection={<Icon icon="lucide:send" width={14} height={14} />}
              >
                <span className="hidden sm:inline">
                  {isUploadingAttachments ? 'Uploading…' : 'Send'}
                </span>
              </GlobalButton>
            </div>
          </div>
        </div>

        {/* Comments List */}
        <div className="space-y-3">
          {!intakeComments || intakeComments.length === 0 ? (
            <div className="border-0.5 border-gray-3/20 rounded-lg p-4 bg-modal-input">
              <p className="text-sm text-text-primary-body">
                No comments yet. Be the first to comment!
              </p>
            </div>
          ) : (
            intakeComments.map((comment) => {
              const isOwnComment =
                comment?.user_id === user?.id 
              const isConfirming = confirmDeleteId === comment._id;

              return (
                <div
                  key={comment._id}
                  className="rounded-lg p-3 bg-black-7/50 shadow"
                >
                  <div className="w-full flex flex-col items-start gap-2">
                    <div className="w-full flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <div className="w-6">
                          <Profile />
                        </div>
                        <p className="text-xs font-semibold text-text-primary-highlight whitespace-nowrap">
                          {comment?.username || 'Unknown User'}
                        </p>

                        <span className="text-[10px] text-input-inactive-placeholder">
                          {formatTimestamp(comment.date_posted)}
                        </span>
                      </div>
                      {isOwnComment && !editingCommentId && (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleStartEdit(comment)}
                            className="p-1 hover:bg-[#141414] rounded text-text-primary-body hover:text-primary transition-colors text-xs"
                            title="Edit comment"
                          >
                            <Icon icon="lucide:pencil" width={14} height={14} />
                          </button>
                          <button
                            onClick={() =>
                              setConfirmDeleteId(
                                isConfirming ? null : comment._id
                              )
                            }
                            className="p-1 hover:bg-[#141414] rounded text-text-primary-body hover:text-error-2 transition-colors text-xs"
                            title="Delete comment"
                          >
                            <Icon
                              icon="lucide:trash-2"
                              width={14}
                              height={14}
                            />
                          </button>
                        </div>
                      )}
                    </div>

                    {editingCommentId === comment._id ? (
                      <div className="w-full flex flex-col gap-2">
                        <div className="border-0.5 border-gray-3/20 rounded-lg bg-modal-input">
                          <div className="p-3">
                            <div
                              ref={editEditorRef}
                              contentEditable
                              className="w-full bg-transparent text-xs text-text-primary-highlight outline-none min-h-[60px]"
                              suppressContentEditableWarning
                            />
                          </div>
                          <div className="border-t border-gray-3/20 px-3 py-2 flex items-center justify-between">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => document.execCommand('bold')}
                                className="p-1.5 hover:bg-[#141414] rounded text-text-primary-body hover:text-gray-300 transition-colors"
                                title="Bold"
                                type="button"
                              >
                                <Icon
                                  icon="lucide:bold"
                                  width={16}
                                  height={16}
                                />
                              </button>
                              <button
                                onClick={() => document.execCommand('italic')}
                                className="p-1.5 hover:bg-[#141414] rounded text-text-primary-body hover:text-gray-300 transition-colors"
                                title="Italic"
                                type="button"
                              >
                                <Icon
                                  icon="lucide:italic"
                                  width={16}
                                  height={16}
                                />
                              </button>
                              <button
                                onClick={() =>
                                  document.execCommand('underline')
                                }
                                className="p-1.5 hover:bg-[#141414] rounded text-text-primary-body hover:text-gray-300 transition-colors"
                                title="Underline"
                                type="button"
                              >
                                <Icon
                                  icon="lucide:underline"
                                  width={16}
                                  height={16}
                                />
                              </button>
                            </div>
                            <div className="flex gap-1">
                              <div>
                                <GlobalButton
                                  variant={'stroke'}
                                  state={
                                    deletingCommentId === comment._id
                                      ? 'inactive'
                                      : 'default'
                                  }
                                  size="sm"
                                  onClick={() => {
                                    setEditingCommentId(null);
                                  }}
                                  disabled={deletingCommentId === comment._id}
                                >
                                  Cancel
                                </GlobalButton>
                              </div>
                              <div>
                                <GlobalButton
                                  size="sm"
                                  onClick={() =>
                                    handleUpdateComment(comment._id)
                                  }
                                  state={
                                    deletingCommentId === comment._id
                                      ? 'inactive'
                                      : 'default'
                                  }
                                  disabled={deletingCommentId === comment._id}
                                >
                                  {deletingCommentId === comment._id
                                    ? 'Saving...'
                                    : 'Save'}
                                </GlobalButton>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : isConfirming ? (
                      <div className="w-full flex items-center gap-2 p-2 bg-[#1a1a1a] rounded border border-error-2/30">
                        <span className="text-xs text-error-2">
                          Delete this comment?
                        </span>
                        <div className="ml-auto flex gap-1">
                          <div>
                            <GlobalButton
                              variant="stroke"
                              size="sm"
                              state={
                                deletingCommentId === comment._id
                                  ? 'inactive'
                                  : 'default'
                              }
                              onClick={() => setConfirmDeleteId(null)}
                              disabled={deletingCommentId === comment._id}
                            >
                              Cancel
                            </GlobalButton>
                          </div>

                          <div>
                            <GlobalButton
                              variant="secondaryRed"
                              state={
                                deletingCommentId === comment._id
                                  ? 'inactive'
                                  : 'default'
                              }
                              onClick={() => handleDeleteComment(comment._id)}
                              disabled={deletingCommentId === comment._id}
                              size="sm"
                            >
                              {deletingCommentId === comment._id
                                ? 'Deleting...'
                                : 'Delete'}
                            </GlobalButton>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <CommentText html={comment?.intake_comment_text} />
                        {intake?._id && comment?._id && (
                          <CommentAttachmentsList
                            parent={{
                              type: 'intake_comment',
                              intakeId: intake._id,
                              commentId: comment._id,
                            }}
                          />
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {totalCount > COMMENTS_PER_PAGE && (
          <PaginationArrows
            hasPrevious={hasPrevious}
            hasNext={hasNext}
            onPreviousPage={handlePreviousPage}
            onNextPage={handleNextPage}
            showingFrom={showingFrom}
            showingTo={showingTo}
            totalItems={totalItems}
            itemLabel="comments"
            className="mt-3"
          />
        )}
      </div>
    </div>
  );
}

const LINE_CLAMP_HEIGHT = 60;

function CommentText({ html }: { html: string }) {
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const contentRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (node) {
        setOverflows(node.scrollHeight > LINE_CLAMP_HEIGHT + 2);
      }
    },
    [html]
  );

  return (
    <div className="flex-1 w-full">
      <div
        ref={contentRef}
        className="text-xs text-text-primary-body leading-relaxed overflow-hidden transition-all duration-200"
        style={{ maxHeight: expanded ? undefined : LINE_CLAMP_HEIGHT }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {overflows && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="mt-1 text-[11px] text-navigation-accent-1 hover:underline"
          type="button"
        >
          {expanded ? 'Read less' : 'Read more'}
        </button>
      )}
    </div>
  );
}
